import { useCallback, useContext, useEffect, useMemo } from 'react'

import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { useLocation, useHistory } from 'react-router'

import { LoadingScreen } from '@/components/ui/loading-screen'
import { logService } from '@/services/log-service'
import { localStorageService, sessionStorageService } from '@/services/storage-service'
import { useAppState } from '@/states/app-state'

import useAccountsApi from '../../api/accountsApi'
import { authService } from '../../core'
import { selectIsLoading } from '../../redux/ducks/appDuck'
import { selectSnackbar } from '../../redux/ducks/snackbarDuck'
import { openNewDownloadModalAC } from '../../redux/ducks/widgetNewDownloadDuck'
import { PAGES } from '../../routes/pages'
import { redirectToLoginTimerClear, redirectToLoginTimerStart } from '../../utils/redirectToLogin'
import { SignalRContext } from '../../utils/signalR'
import { getErrorSnackbarOptions, getSuccessSnackbarOptions } from '../../utils/snackbarNotifications'

export const RootLayout = (props) => {
  const profile = useAppState((state) => state.profile)
  const setProfile = useAppState((state) => state.setProfile)
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const snackbar = useSelector((state) => selectSnackbar(state))
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const signalRConnection = useContext(SignalRContext)
  const accountsApi = useAccountsApi()
  const showLoading = useSelector(selectIsLoading)

  const children = useMemo(() => {
    return props.children
  }, [props])

  useEffect(() => {
    setClarityProperties(profile)
  }, [profile])

  useEffect(() => {
    // Hide Google reCAPTCHA if logged in
    if (authService.isCompanyAuthenticated() || authService.isCpaUser() || authService.isEssUser()) {
      if (!document.querySelector('body').classList.contains('logged-in')) {
        document.querySelector('body').classList.add('logged-in')
      }
    } else if (document.querySelector('body').classList.contains('logged-in')) {
      document.querySelector('body').classList.remove('logged-in')
    }

    // Hide Chat Support link if on login page
    if (location.pathname === PAGES.brandsLogin.path) {
      if (!document.querySelector('body').classList.contains('login-page')) {
        document.querySelector('body').classList.add('login-page')
      }
    } else if (document.querySelector('body').classList.contains('login-page')) {
      document.querySelector('body').classList.remove('login-page')
    }
  }, [location])

  useEffect(() => {
    const unregisterHistoryListener = history.listen(async (location, action) => {
      logService.info('Route change detected', { location, action })
      try {
        if (signalRConnection?.connectionState !== 'Disconnected') {
          await signalRConnection?.invoke('SetUrl', location.pathname)
        }
      } catch (error) {
        logService.error('Error updating SignalR with new URL', { error })
      }
    })
    return () => {
      unregisterHistoryListener()
    }
  }, [])

  useEffect(() => {
    if (snackbar.show === true) {
      if (snackbar.variant === 'success') {
        enqueueSnackbar(snackbar.message, getSuccessSnackbarOptions(closeSnackbar))
      } else if (snackbar.variant === 'failure') {
        enqueueSnackbar(snackbar.message, getErrorSnackbarOptions(closeSnackbar))
      }
    }
  }, [snackbar])

  const handleReportNotification = useCallback(
    (data) => {
      dispatch(openNewDownloadModalAC(data))
    },
    [dispatch],
  )

  const handleForceRefreshConnection = useCallback(() => {
    window.location.reload(true)
  }, [])

  // SignalR
  useEffect(() => {
    if (!signalRConnection) return
    try {
      signalRConnection.on('ReportNotification', handleReportNotification)
      signalRConnection.on('ForceRefreshConnection', handleForceRefreshConnection)
    } catch (error) {
      logService.error('Error registering SignalR event handlers', { error })
    }
    return () => {
      signalRConnection.off('ReportNotification', handleReportNotification)
      signalRConnection.off('ForceRefreshConnection', handleForceRefreshConnection)
    }
  }, [signalRConnection, handleReportNotification, handleForceRefreshConnection])

  useEffect(() => {
    const getProfile = async () => await accountsApi.getProfile()
    const isAuthenticated = authService.isCompanyAuthenticated() || authService.isCpaUser() || authService.isEssUser()
    if (isAuthenticated && !profile) {
      getProfile()
      redirectToLoginTimerStart(history)
    } else if (!isAuthenticated) {
      setProfile(null)
    }
    return () => {
      return redirectToLoginTimerClear
    }
  }, [])

  useEffect(() => {
    let lockResolver
    if (navigator && navigator.locks && navigator.locks.request) {
      const promise = new Promise((res) => {
        lockResolver = res
      })

      navigator.locks.request('brands_lock', { mode: 'shared' }, () => {
        return promise
      })
    }
  }, [])

  return (
    <>
      <LoadingScreen show={showLoading} />
      {children}
    </>
  )
}

function setClarityProperties(profile) {
  if (!window?.clarity) return

  window.clarity('set', 'bfp', localStorageService.getBfp())
  window.clarity('set', 'sfp', sessionStorageService.getSfp())
  window.clarity('set', 'ClientApp_Version', process.env.REACT_APP_VERSION)

  const jwtGuid = authService.getJwtGuid()
  if (jwtGuid) window.clarity('set', 'JWT_Guid', jwtGuid)

  if (profile) {
    window.clarity(
      'identify',
      profile?.userName,
      localStorageService.getBfp(),
      null,
      `${profile.employee?.firstName} ${profile.employee?.lastName}`,
    )
  } else {
    window.clarity('identify', null, localStorageService.getBfp())
  }

  if (!profile) return
  window.clarity('set', 'user_name', profile?.userName)
  window.clarity('set', 'API_Version', profile?.version)
  if (profile?.conum) {
    window.clarity('set', 'conum', profile?.conum.toString())
  }
}
